import { Check, AlertTriangle, X } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BenchmarkProduct } from '@/data/benchmarkData';

interface BenchmarkTableProps {
  products: BenchmarkProduct[];
}

export const BenchmarkTable = ({ products }: BenchmarkTableProps) => {
  const getStatusIcon = (status: 'compliant' | 'partial' | 'not_compliant') => {
    switch (status) {
      case 'compliant':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'not_compliant':
        return <X className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusColor = (status: 'compliant' | 'partial' | 'not_compliant') => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_compliant':
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">
            No products found matching your benchmark criteria.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Benchmark Compliance</CardTitle>
        <p className="text-sm text-muted-foreground">
          Compliance status across ecolabels, retailer policies, and state mandates
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Product</TableHead>
                <TableHead className="min-w-[250px]">Ecolabels</TableHead>
                <TableHead className="min-w-[200px]">Retailers</TableHead>
                <TableHead className="min-w-[120px]">States</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} className="hover:bg-muted/50">
                  <TableCell>
                    <div>
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {product.manufacturer}
                      </div>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {product.product_type.replace('_', ' ')}
                      </Badge>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="space-y-1">
                      {product.ecolabels.map((ecolabel, index) => (
                        <div key={index} className="flex items-center gap-2">
                          {getStatusIcon(ecolabel.status)}
                          <span className="text-sm">{ecolabel.name}</span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="space-y-1">
                      {product.retailers.map((retailer, index) => (
                        <div key={index} className="flex items-center gap-2">
                          {getStatusIcon(retailer.status)}
                          <span className="text-sm">{retailer.name}</span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(product.states.status)}
                      <div>
                        <div className="font-medium">{product.states.count} states</div>
                        {product.states.details && (
                          <div className="text-xs text-muted-foreground">
                            {product.states.details}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {/* Legend */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-medium mb-3">Legend</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              <span>Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <span>Partially Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 text-red-600" />
              <span>Not Compliant</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};